import { MAX_APPARIEMENT, partenaireApparie } from './appariement'
import { ecartJours } from './calendrier'
import {
  type BorneChange,
  DEVISE_DES_BORNES,
  FENETRE_SURE_DEVISE,
  nommeLeFournisseur,
} from './justificatifs'
import { contientNumero, normaliserLibelle } from './libelle'

// Rapprochement : paiements ouverts × transaction non affectée → propositions notées.
// Un seul candidat sûr : automatique. Plusieurs : décision humaine. Les affectations sont
// signées comme la transaction, en centimes de sa devise : leur somme vaut son montant.
// Sûr : montant exact dans la même devise ; ou, décision de Pierre-Marie du 14/10/2026, facture
// dans une devise bornée (dollars) débitée en euros, comme la correspondance stricte des
// justificatifs et de « Réglée par la société ? » : change dans la bande, fournisseur nommé au
// sens strict par le libellé, débit dans la fenêtre sûre de la devise, seul candidat sûr de
// l'opération et l'opération seule à l'être pour ce paiement, ou, plusieurs factures et plusieurs
// débits d'un même fournisseur, l'appariement sûr de leur composante (`deciderRapprochement`,
// `partenaireApparie`). La bande de change du débit (`BORNES_CHANGE_DEBIT`) est la sienne.

/**
 * Bande de change d'un débit en euros d'une facture en devise, pour le rapprochement bancaire et
 * le règlement par débit (décision de Pierre-Marie du 14/10/2026) : en millièmes, comme
 * `BORNES_CHANGE` des justificatifs, qui reste inchangée, mais plus basse : en janvier et février
 * 2026, les débits d'une facture en dollars tombaient à 844 à 849 ‰.
 */
export const BORNES_CHANGE_DEBIT: Readonly<Record<string, BorneChange>> = {
  USD: { min: 830, max: 940 },
}

const borneDebit = (devise: string): BorneChange | null =>
  Object.hasOwn(BORNES_CHANGE_DEBIT, devise) ? (BORNES_CHANGE_DEBIT[devise] ?? null) : null

/** Bande du débit d'un montant en `devise` réglé en `deviseOperation` (euros), sinon null. */
export function bornesDeChangeDebit(devise: string, deviseOperation: string): BorneChange | null {
  return deviseOperation === DEVISE_DES_BORNES && devise !== deviseOperation
    ? borneDebit(devise)
    : null
}

/** Débit en euros dans la bande du débit d'un montant en devise, bornes comprises ; calcul entier. */
export function changeDansLaBandeDebit(eurCts: number, devCts: number, devise: string): boolean {
  const b = borneDebit(devise)
  if (!b || !Number.isSafeInteger(eurCts) || !Number.isSafeInteger(devCts)) return false
  if (eurCts <= 0 || devCts <= 0) return false
  const eur = BigInt(eurCts) * 1000n
  const dev = BigInt(devCts)
  return eur >= dev * BigInt(b.min) && eur <= dev * BigInt(b.max)
}

export type PaiementOuvert = {
  id: string
  sens: 'payer' | 'encaisser'
  /** Reste dû, positif. */
  resteCts: number
  devise: string
  datePrevue: string | null
  tiersId: string | null
  libellesTiers: readonly string[]
  ibanTiers: string | null
  /** Numéro de la facture ou de l'avis rattaché. */
  numero: string | null
  /** Libellé saisi du paiement (« ARTISAN ») : ses mots significatifs comptent comme signal. */
  libelle?: string | null
  /** Réglé par carte à la commande (carte lue sur la facture) : débité autour de sa date. */
  parCarte?: boolean
  /**
   * Noms du fournisseur (tiers, émetteur lu sur la pièce) : nommé au sens strict par le libellé
   * de l'opération (`nommeLeFournisseur`), il rend sûr le débit en euros d'une facture en devise.
   */
  nomsTiers?: readonly string[]
}

export type TransactionARapprocher = {
  id: string
  dateOperation: string
  /** Montant non encore affecté, signé (négatif au débit). */
  montantCts: number
  devise: string
  libelleNormalise: string
  ibanContrepartie: string | null
}

export type Affectation = { paiementId: string; montantAffecteCts: number }
export type Proposition = {
  affectations: Affectation[]
  score: number
  raisons: string[]
  exact: boolean
  /**
   * Débit en euros d'un paiement en devise bornée, sûr comme une correspondance stricte des
   * justificatifs : change dans la bande, fournisseur nommé au sens strict, fenêtre sûre.
   */
  sureParLeChange?: boolean
  /**
   * Même montant dans la même devise, à `JOURS_SUR_PAR_LA_DATE` jours au plus de la date prévue,
   * le fournisseur reconnu dans le libellé (signe du tiers, ou nom au sens large) : sûr s'il est
   * seul de part et d'autre (`deciderRapprochement`).
   */
  sureParLaDate?: boolean
}

const SEUIL_SUR = 70
const ECART_SUR = 20
const SEUIL_CANDIDAT = 40

/**
 * Au-delà de ce nombre de jours entre la date prévue et l'opération, un paiement n'est proposé
 * que si les montants concordent : identiques, ou l'écart s'explique par le change (décision de
 * Pierre-Marie du 29/10/2026). Un montant seulement proche ou partiel, si loin, est une coïncidence.
 */
export const JOURS_MAX_SANS_CONCORDANCE = 90

/**
 * Même montant, à 3 jours près de la date prévue, et le fournisseur reconnu dans le libellé, même
 * au sens large : rapproché seul s'il est seul de part et d'autre (décision de Pierre-Marie du
 * 29/10/2026 : un abonnement reconnu au jour près ne demande plus de décision parce qu'un mois
 * précédent au même montant reste ouvert).
 */
export const JOURS_SUR_PAR_LA_DATE = 3

/**
 * Sans aucun signe du fournisseur (ni IBAN, ni libellé, ni nom même au sens large, ni numéro), un
 * paiement n'est proposé qu'à ce nombre de jours au plus de sa date prévue : plus loin, un même
 * montant est une coïncidence (décision de Pierre-Marie du 29/10/2026).
 */
export const JOURS_MAX_SANS_SIGNE = 15

/**
 * Couverture d'un paiement réglé par une opération dans une autre devise : au bas de la bande de
 * change du débit (`BORNES_CHANGE_DEBIT`), en millièmes. En données : la couverture lue en base
 * (service des paiements, `packages/db`) suit la même table.
 */
export const CONVERSIONS_COUVERTURE: readonly {
  devise: string
  deviseOperation: string
  millieme: number
}[] = Object.entries(BORNES_CHANGE_DEBIT).map(([devise, b]) => ({
  devise,
  deviseOperation: DEVISE_DES_BORNES,
  millieme: b.min,
}))

/**
 * Part d'un paiement que couvre une affectation, dans la devise du paiement. L'affectation est en
 * centimes de l'opération : même devise, telle quelle ; débit en euros d'un paiement dans une
 * devise bornée (facture en dollars), converti au bas de la bande : un débit dans la bande, ou
 * au-dessus, solde le paiement, un débit plus faible le couvre en partie. Autre devise, sans
 * bande : telle quelle, faute de taux. Signée, tronquée vers zéro (division entière de Postgres).
 */
export function couvertDansLaDevise(
  affecteCts: number,
  deviseOperation: string,
  devisePaiement: string,
): number {
  if (deviseOperation === devisePaiement) return affecteCts
  const c = CONVERSIONS_COUVERTURE.find(
    (x) => x.devise === devisePaiement && x.deviseOperation === deviseOperation,
  )
  return c ? Number((BigInt(affecteCts) * 1000n) / BigInt(c.millieme)) : affecteCts
}

function signeDe(p: PaiementOuvert) {
  return p.sens === 'encaisser' ? 1 : -1
}

/**
 * L'un des libellés bancaires du tiers figure dans le libellé normalisé de l'opération : la
 * règle du rapprochement, reprise par la recherche des pièces dans les mails.
 */
export function libelleDuTiersReconnu(
  libellesTiers: readonly string[],
  libelleNormalise: string,
): boolean {
  return libellesTiers.some(
    (l) => l.trim() !== '' && libelleNormalise.includes(normaliserLibelle(l)),
  )
}

function signalTiers(t: TransactionARapprocher, p: PaiementOuvert): string[] {
  const raisons: string[] = []
  if (
    p.ibanTiers &&
    t.ibanContrepartie &&
    p.ibanTiers.replace(/\s/g, '') === t.ibanContrepartie.replace(/\s/g, '')
  ) {
    raisons.push('IBAN')
  }
  if (libelleDuTiersReconnu(p.libellesTiers, t.libelleNormalise)) {
    raisons.push('libellé du tiers')
  }
  const mots = normaliserLibelle(p.libelle ?? '')
    .split(/[^A-Z0-9]+/)
    .filter((m) => m.length >= 4 && !/^\d+$/.test(m))
  const motsTransaction = new Set(t.libelleNormalise.split(/[^A-Z0-9]+/))
  if (mots.length > 0 && mots.some((m) => motsTransaction.has(m)))
    raisons.push('libellé du paiement')
  return raisons
}

/** Proposition pour un seul paiement, ou null si incompatible. */
function evaluer(t: TransactionARapprocher, p: PaiementOuvert): Proposition | null {
  if (Math.sign(t.montantCts) !== signeDe(p) || p.resteCts <= 0) return null
  const montant = Math.abs(t.montantCts)
  const raisons: string[] = []
  let score = 0
  let exact = false
  // Montants identiques, ou dans deux devises à l'écart du change près (`JOURS_MAX_SANS_CONCORDANCE`).
  let concordant = false
  let affecte = montant

  const tolerance =
    p.devise !== t.devise
      ? Math.round(p.resteCts * 0.03)
      : Math.max(200, Math.round(p.resteCts * 0.005))
  // Paiement dans une devise bornée réglé en euros (facture en dollars, débit de carte) : ses
  // centimes ne se comparent à ceux de l'opération qu'à travers la bande de change.
  const bornes = bornesDeChangeDebit(p.devise, t.devise)
  const tiers = signalTiers(t, p)
  // Devise bornée : le fournisseur nommé au sens strict, comme dans les justificatifs.
  const nomme =
    bornes !== null &&
    nommeLeFournisseur(
      { libelle: t.libelleNormalise, marchand: null },
      { noms: [...(p.nomsTiers ?? [])], libellesBancaires: [...p.libellesTiers] },
      'strict',
    )
  const dansLaBande = bornes !== null && changeDansLaBandeDebit(montant, p.resteCts, p.devise)
  if (montant === p.resteCts && p.devise === t.devise) {
    score += 50
    exact = true
    concordant = true
    raisons.push('montant exact')
  } else if (
    bornes
      ? // La bande est large : un montant dedans n'est une piste qu'avec un signe du tiers.
        (tiers.length > 0 || nomme) && dansLaBande
      : Math.abs(montant - p.resteCts) <= tolerance &&
        // Same currency: an approximate amount alone is a coincidence (any 30 € card payment
        // near a 29 € phone bill), not a lead. It needs a sign of the payee.
        (p.devise !== t.devise || tiers.length > 0)
  ) {
    score += 25
    raisons.push('montant proche (frais ou devise)')
    concordant = p.devise !== t.devise
    // En devise bornée, toute l'opération : elle solde le paiement (`couvertDansLaDevise`).
    affecte = bornes ? montant : Math.min(montant, p.resteCts)
  } else if (couvertDansLaDevise(montant, t.devise, p.devise) < p.resteCts && tiers.length > 0) {
    score += 10
    raisons.push('paiement partiel')
  } else {
    return null
  }

  // Le fournisseur reconnu dans le libellé, même au sens large.
  const nommeLarge = nommeLeFournisseur(
    { libelle: t.libelleNormalise, marchand: null },
    { noms: [...(p.nomsTiers ?? [])], libellesBancaires: [...p.libellesTiers] },
    'large',
  )
  const numero = p.numero !== null && contientNumero(t.libelleNormalise, p.numero)
  const signe = tiers.length > 0 || nommeLarge || numero

  if (p.datePrevue) {
    const ecart = ecartJours(p.datePrevue, t.dateOperation)
    if (Math.abs(ecart) > JOURS_MAX_SANS_CONCORDANCE && !concordant) return null
    if (Math.abs(ecart) > JOURS_MAX_SANS_SIGNE && !signe) return null
    let points: number
    if (p.sens === 'payer') {
      if (ecart < -15) return null
      points = Math.abs(ecart) <= 5 ? 15 : Math.abs(ecart) <= 15 ? 8 : 0
    } else {
      // Encaissements : pas de limite de date après l'échéance.
      if (ecart < -30) return null
      points = ecart >= -5 && ecart <= 10 ? 15 : 5
    }
    score += points
    if (points > 0) raisons.push('date')
  }

  if (tiers.includes('IBAN')) score += 25
  if (tiers.includes('libellé du tiers')) score += 20
  if (tiers.includes('libellé du paiement')) score += 15
  raisons.push(...tiers)
  // Nommé sans autre signe du tiers : compte comme le libellé du paiement.
  if (nomme && tiers.length === 0) {
    score += 15
    raisons.push('fournisseur nommé')
  }
  if (numero) {
    score += 25
    raisons.push('numéro de facture')
  }
  const sureParLeChange =
    nomme &&
    dansLaBande &&
    p.datePrevue !== null &&
    dansLaFenetre(ecartJours(p.datePrevue, t.dateOperation), FENETRE_SURE_DEVISE)
  if (sureParLeChange) raisons.push('change sûr')
  const sureParLaDate =
    exact &&
    p.datePrevue !== null &&
    Math.abs(ecartJours(p.datePrevue, t.dateOperation)) <= JOURS_SUR_PAR_LA_DATE &&
    (tiers.length > 0 || nommeLarge)
  if (sureParLaDate) raisons.push('date et fournisseur')

  return {
    affectations: [{ paiementId: p.id, montantAffecteCts: Math.sign(t.montantCts) * affecte }],
    score: Math.min(100, score),
    raisons,
    exact,
    ...(sureParLeChange && { sureParLeChange }),
    ...(sureParLaDate && { sureParLaDate }),
  }
}

/** Écart (jours, positif quand l'opération suit la date prévue) dans la fenêtre, bornes comprises. */
const dansLaFenetre = (ecart: number, f: { min: number; max: number }) =>
  ecart >= f.min && ecart <= f.max

/**
 * Regroupements : plusieurs paiements du même tiers dont la somme signée vaut exactement
 * la transaction (reversement de loyers net d'honoraires).
 */
function regroupements(t: TransactionARapprocher, paiements: PaiementOuvert[]): Proposition[] {
  const candidats = paiements
    .filter((p) => p.devise === t.devise && p.resteCts > 0 && signalTiers(t, p).length > 0)
    .slice(0, 14)
  const resultats: Proposition[] = []
  const n = candidats.length
  for (let masque = 1; masque < 1 << n; masque++) {
    const taille = popcount(masque)
    if (taille < 2 || taille > 6) continue
    let somme = 0
    const choisis: PaiementOuvert[] = []
    for (let i = 0; i < n; i++) {
      if (masque & (1 << i)) {
        const p = candidats[i]!
        somme += signeDe(p) * p.resteCts
        choisis.push(p)
      }
    }
    if (somme !== t.montantCts) continue
    resultats.push({
      affectations: choisis.map((p) => ({
        paiementId: p.id,
        montantAffecteCts: signeDe(p) * p.resteCts,
      })),
      score: 85,
      raisons: ['regroupement exact', ...new Set(choisis.flatMap((p) => signalTiers(t, p)))],
      exact: true,
    })
  }
  return resultats
}

/** Achats du même tiers débités ensemble : fenêtre de dates, taille, score (jamais automatique). */
const JOURS_ACHATS_GROUPES = 2
const MAX_ACHATS_GROUPES = 4
const SCORE_ACHATS_GROUPES = 60

/**
 * Achats du même tiers débités ensemble (une carte, une place de marché qui réunit plusieurs
 * factures du jour) : sans signe du tiers dans le libellé, une seule combinaison de 2 à 4
 * paiements du même tiers, à deux jours près, dont la somme vaut exactement la transaction ;
 * proposée à un humain, jamais validée seule (score sous le seuil sûr). Plusieurs : rien.
 */
function achatsGroupes(t: TransactionARapprocher, paiements: PaiementOuvert[]): Proposition[] {
  const parTiers = new Map<string, PaiementOuvert[]>()
  for (const p of paiements) {
    if (!p.tiersId || !p.datePrevue || p.devise !== t.devise || p.resteCts <= 0) continue
    if (signeDe(p) !== Math.sign(t.montantCts)) continue
    if (Math.abs(ecartJours(p.datePrevue, t.dateOperation)) > JOURS_ACHATS_GROUPES) continue
    parTiers.set(p.tiersId, [...(parTiers.get(p.tiersId) ?? []), p])
  }
  const trouvees: PaiementOuvert[][] = []
  for (const candidats of parTiers.values()) {
    const liste = candidats.slice(0, 16)
    for (let masque = 1; masque < 1 << liste.length; masque++) {
      const taille = popcount(masque)
      if (taille < 2 || taille > MAX_ACHATS_GROUPES) continue
      const choisis = liste.filter((_, i) => masque & (1 << i))
      const somme = choisis.reduce((s, p) => s + signeDe(p) * p.resteCts, 0)
      if (somme !== t.montantCts) continue
      trouvees.push(choisis)
      if (trouvees.length > 1) return []
    }
  }
  const [choisis] = trouvees
  if (!choisis) return []
  return [
    {
      affectations: choisis.map((p) => ({
        paiementId: p.id,
        montantAffecteCts: signeDe(p) * p.resteCts,
      })),
      score: SCORE_ACHATS_GROUPES,
      raisons: ['achats du même tiers, somme exacte', 'date'],
      exact: true,
    },
  ]
}

function popcount(x: number) {
  let n = 0
  while (x) {
    n += x & 1
    x >>= 1
  }
  return n
}

/** Paiement réglé par carte à la commande : le débit suit sa date à une semaine près. */
const JOURS_CARTE = 7

/**
 * Un paiement réglé par carte à la commande n'est débité qu'autour de sa date : plus loin, le
 * débit est un autre achat au même montant (abonnement, achat répété), et l'ancien achat, payé
 * mais jamais couvert par un relevé, n'est candidat ni seul ni dans un regroupement.
 */
function dansLaFenetreCarte(t: TransactionARapprocher, p: PaiementOuvert): boolean {
  if (!p.parCarte || !p.datePrevue) return true
  return Math.abs(ecartJours(p.datePrevue, t.dateOperation)) <= JOURS_CARTE
}

export function proposer(t: TransactionARapprocher, ouverts: PaiementOuvert[]): Proposition[] {
  const paiements = ouverts.filter((p) => dansLaFenetreCarte(t, p))
  const simples = paiements.map((p) => evaluer(t, p)).filter((p): p is Proposition => p !== null)
  const meilleur = Math.max(0, ...simples.map((p) => p.score))
  const groupes = meilleur >= SEUIL_SUR ? [] : regroupements(t, paiements)
  if (groupes.length > 1) for (const g of groupes) g.score = 60
  // Sans signe du tiers dans le libellé : achats du même tiers débités ensemble, proposés.
  const achats = meilleur >= SEUIL_SUR || groupes.length > 0 ? [] : achatsGroupes(t, paiements)
  return [...simples, ...groupes, ...achats].sort((a, b) => b.score - a.score)
}

/**
 * L'affectation d'une transaction au paiement qu'un humain a désigné en choisissant la pièce d'une
 * opération du cabinet appariée à cette transaction (« C'est celle-ci », décision de Pierre-Marie
 * du 13/10/2026 : l'opération ne sert que de pont) : seulement si la règle propose ce paiement
 * pour la transaction (proposition simple au moins candidate, fenêtre de la carte comprise), ou,
 * pour un paiement dans une devise bornée (facture en dollars) débité en euros, si le débit est
 * dans la bande de change : le choix de la pièce vaut alors le signe du tiers, et toute
 * l'opération lui est affectée (`couvertDansLaDevise`). Null sinon : rien n'est relié côté banque.
 */
export function affectationParChoix(
  t: TransactionARapprocher,
  p: PaiementOuvert,
): Proposition | null {
  const proposee = proposer(t, [p]).find(
    (x) => x.affectations.length === 1 && x.score >= SEUIL_CANDIDAT,
  )
  if (proposee) return proposee
  if (Math.sign(t.montantCts) !== signeDe(p) || p.resteCts <= 0) return null
  if (!bornesDeChangeDebit(p.devise, t.devise) || !dansLaFenetreCarte(t, p)) return null
  if (!changeDansLaBandeDebit(Math.abs(t.montantCts), p.resteCts, p.devise)) return null
  return {
    affectations: [{ paiementId: p.id, montantAffecteCts: t.montantCts }],
    score: SEUIL_CANDIDAT,
    raisons: ['change dans la bande', 'pièce choisie par un humain'],
    exact: false,
  }
}

export type Decision =
  | { mode: 'auto'; proposition: Proposition }
  | { mode: 'decision'; propositions: Proposition[] }
  | { mode: 'aucun' }

export function decider(propositions: Proposition[]): Decision {
  const [premier, second] = propositions
  if (!premier || premier.score < SEUIL_CANDIDAT) return { mode: 'aucun' }
  const sur =
    premier.exact &&
    premier.score >= SEUIL_SUR &&
    (!second || second.score <= premier.score - ECART_SUR)
  // Sûr par le change : la seule proposition sûre, aucune autre exacte (montant, regroupement).
  const surParLeChange =
    premier.sureParLeChange === true &&
    propositions.slice(1).every((p) => !p.sureParLeChange && !p.exact)
  if (sur || surParLeChange) return { mode: 'auto', proposition: premier }
  // Sûr par la date : la seule proposition sûre par la date, aucune sûre par le change.
  const parLaDate = propositions.filter((p) => p.sureParLaDate)
  if (parLaDate.length === 1 && propositions.every((p) => !p.sureParLeChange))
    return { mode: 'auto', proposition: parLaDate[0]! }
  return enDecision(propositions)
}

const enDecision = (propositions: Proposition[]): Decision => ({
  mode: 'decision',
  propositions: propositions.filter((p) => p.score >= SEUIL_CANDIDAT).slice(0, 5),
})

/**
 * La décision pour une opération parmi les paiements ouverts (`decider` sur ses propositions).
 * Par le change, la décision tient à la composante qui relie l'opération, de proche en proche par
 * des paires sûres par le change, aux paiements ouverts et aux `autres` opérations ouvertes (deux
 * débits dans la bande la même semaine, deux factures du même fournisseur) : un seul candidat sûr
 * et personne d'autre, automatique ; sinon son appariement sûr (`partenaireApparie` : unique, ou
 * dans l'ordre des dates) ; sinon un humain. Jamais quand un candidat exact la concurrence.
 * L'opération elle-même, si `autres` la contient, est écartée par son identifiant.
 */
export function deciderRapprochement(
  t: TransactionARapprocher,
  ouverts: PaiementOuvert[],
  autres: readonly TransactionARapprocher[] = [],
): Decision {
  const propositions = proposer(t, ouverts)
  const d = decider(propositions)
  if (d.mode === 'aucun') return d
  // Sûr par la date : aucune autre opération ouverte ne l'est pour le même paiement.
  if (d.mode === 'auto' && d.proposition.sureParLaDate && !d.proposition.sureParLeChange) {
    const pid = d.proposition.affectations[0]!.paiementId
    const p = ouverts.find((x) => x.id === pid)!
    const rivale = autres.some(
      (a) => a.id !== t.id && proposer(a, [p]).some((x) => x.sureParLaDate),
    )
    return rivale ? enDecision(propositions) : d
  }
  if (d.mode === 'auto' && !d.proposition.sureParLeChange) return d
  if (!propositions.some((x) => x.sureParLeChange) || propositions.some((x) => x.exact)) return d
  const operations = [t, ...autres.filter((a) => a.id !== t.id)]
  const aretes = aretesParLeChange(t, operations, ouverts)
  const partenaire = partenaireApparie(
    t.id,
    operations.map((o) => ({ id: o.id, date: o.dateOperation })),
    ouverts.flatMap((p) => (p.datePrevue ? [{ id: p.id, date: p.datePrevue }] : [])),
    aretes,
  )
  const retenue = propositions.find(
    (x) => x.sureParLeChange && x.affectations[0]?.paiementId === partenaire,
  )
  return retenue ? { mode: 'auto', proposition: retenue } : enDecision(propositions)
}

/**
 * Les paires (opération, paiement) sûres par le change de la composante de `t`, parcourue de proche
 * en proche ; une opération n'est évaluée contre un paiement que dans la fenêtre sûre de la devise.
 * Le parcours s'arrête au-delà de `MAX_APPARIEMENT` d'un côté : la composante est alors trop
 * grande, `partenaireApparie` la refuse.
 */
function aretesParLeChange(
  t: TransactionARapprocher,
  operations: readonly TransactionARapprocher[],
  ouverts: PaiementOuvert[],
): { gauche: string; droite: string; stricte: true }[] {
  const aretes = new Map<string, { gauche: string; droite: string; stricte: true }>()
  const relier = (gauche: string, droite: string) =>
    aretes.set(`${gauche}\u0000${droite}`, { gauche, droite, stricte: true })
  const vues = new Set([t.id])
  const vus = new Set<string>()
  const aVoir: TransactionARapprocher[] = [t]
  while (aVoir.length > 0 && vues.size <= MAX_APPARIEMENT && vus.size <= MAX_APPARIEMENT) {
    const o = aVoir.shift()!
    for (const x of proposer(o, ouverts)) {
      if (!x.sureParLeChange) continue
      const pid = x.affectations[0]!.paiementId
      relier(o.id, pid)
      if (vus.has(pid)) continue
      vus.add(pid)
      const p = ouverts.find((y) => y.id === pid)!
      for (const a of operations) {
        if (vues.has(a.id) || !p.datePrevue) continue
        if (!dansLaFenetre(ecartJours(p.datePrevue, a.dateOperation), FENETRE_SURE_DEVISE)) continue
        if (!proposer(a, [p]).some((y) => y.sureParLeChange)) continue
        // Reliée dès qu'elle est vue : un parcours arrêté au plafond garde toute sa taille.
        relier(a.id, pid)
        vues.add(a.id)
        aVoir.push(a)
      }
    }
  }
  return [...aretes.values()]
}
